import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Loader2, CheckCircle2, Mail, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Logo } from '../components/Logo';
import { formatApiError } from '../lib/apiError';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  // 'auto' (verifying token from URL), 'idle' (no token in URL), 'success', 'error'
  const [status, setStatus] = useState(token ? 'auto' : 'idle');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [resending, setResending] = useState(false);
  const navigate = useNavigate();
  const { setSessionFromVerification } = useAuth();
  const ranRef = useRef(false);

  // Auto-verify if token is in the URL
  useEffect(() => {
    if (!token) return;
    if (ranRef.current) return;
    ranRef.current = true;
    (async () => {
      try {
        const res = await axios.post(`${API_URL}/api/auth/verify-email`, { token });
        handleSuccess(res.data);
      } catch (err) {
        setStatus('error');
        setMessage(formatApiError(err, 'Verification failed'));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleSuccess = (data) => {
    const { token: jwt, user } = data;
    if (jwt && user && setSessionFromVerification) {
      setSessionFromVerification(jwt, user);
    }
    setStatus('success');
    setMessage(data?.message || 'Email verified! Welcome to ShiftCover365.');
    setTimeout(() => {
      const dest = user?.role === 'manager' ? '/manager' : user?.role === 'admin' ? '/admin' : '/worker';
      navigate(dest);
    }, 1500);
  };

  const handleResend = async () => {
    if (!email) {
      toast.error('Enter your email first');
      return;
    }
    setResending(true);
    try {
      await axios.post(`${API_URL}/api/auth/resend-verification`, { email });
      toast.success("If an account needs verification, we've sent a fresh email.");
    } catch {
      toast.success("If an account needs verification, we've sent a fresh email.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F1F5F9] flex flex-col">
      <header className="bg-[#0f1b3d] text-white px-6 py-5">
        <Logo size="sm" variant="light" linkTo="/" />
      </header>
      <main className="flex-1 flex items-center justify-center p-6">
        <Card className="w-full max-w-md shadow-lg border-0" data-testid="verify-email-card">
          {status === 'auto' && (
            <CardHeader className="text-center space-y-1 pb-6">
              <Loader2 className="h-14 w-14 mx-auto text-[#0f1b3d] animate-spin mb-3" />
              <CardTitle className="text-2xl font-bold text-slate-900">Verifying your email...</CardTitle>
              <CardDescription>One moment please.</CardDescription>
            </CardHeader>
          )}

          {status === 'success' && (
            <CardHeader className="text-center space-y-1 pb-6">
              <div className="mx-auto h-16 w-16 bg-emerald-100 rounded-full flex items-center justify-center mb-3">
                <CheckCircle2 className="h-8 w-8 text-emerald-600" />
              </div>
              <CardTitle className="text-2xl font-bold text-slate-900" data-testid="verify-email-success-title">
                Email verified!
              </CardTitle>
              <CardDescription>{message} Redirecting to onboarding...</CardDescription>
            </CardHeader>
          )}

          {(status === 'idle' || status === 'error') && (
            <>
              <CardHeader className="space-y-1 pb-4">
                <div className="mx-auto h-14 w-14 bg-[#0f1b3d]/10 rounded-full flex items-center justify-center mb-2">
                  <Mail className="h-7 w-7 text-[#0f1b3d]" />
                </div>
                <CardTitle className="text-2xl font-bold text-slate-900 text-center font-['Public_Sans']">
                  {status === 'error' ? "Link didn't work?" : 'Check your email'}
                </CardTitle>
                <CardDescription className="text-center text-slate-500">
                  {status === 'error' ? (
                    <span data-testid="verify-email-error-message">
                      {message} Request a fresh verification email below.
                    </span>
                  ) : (
                    <>
                      We&apos;ve sent a verification link to your inbox. Tap the
                      <strong className="text-slate-700"> Verify email </strong>
                      button in the email to activate your account.
                    </>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 mb-5">
                  <p className="text-[12.5px] text-slate-600 leading-relaxed">
                    Didn&apos;t receive the email? Check your spam folder, or enter your email below to send a new verification link.
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-700 font-medium">Your email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoComplete="email"
                      className="h-12 rounded-xl bg-slate-50 border-slate-200"
                      data-testid="verify-email-address-input"
                    />
                  </div>

                  <Button
                    type="button"
                    onClick={handleResend}
                    disabled={resending || !email}
                    className="w-full h-12 rounded-full bg-[#DA291C] hover:bg-[#b91f13] text-white font-semibold"
                    data-testid="verify-email-resend-btn"
                  >
                    {resending ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending...</>
                    ) : (
                      <><RefreshCw className="mr-2 h-4 w-4" />Send new verification link</>
                    )}
                  </Button>
                </div>

                <p className="mt-5 text-center text-sm text-slate-500">
                  <Link to="/login" className="text-[#DA291C] font-semibold hover:underline" data-testid="verify-email-back-to-login">
                    Back to sign in
                  </Link>
                </p>
              </CardContent>
            </>
          )}
        </Card>
      </main>
    </div>
  );
}
